var __require = /* @__PURE__ */ ((x) => typeof require !== "undefined" ? require : typeof Proxy !== "undefined" ? new Proxy(x, {
  get: (a, b) => (typeof require !== "undefined" ? require : a)[b]
}) : x)(function(x) {
  if (typeof require !== "undefined")
    return require.apply(this, arguments);
  throw Error('Dynamic require of "' + x + '" is not supported');
});

// src/components/IconButton/IconButton.react.tsx
import React3, { forwardRef as forwardRef3 } from "react";
import { tv as tv8 } from "tailwind-variants";

// src/styles/utils/color.ts
import { tv as tv2 } from "tailwind-variants";

// src/utils/object.ts
var keys = (obj) => Object.keys(obj);
var fromEntries = (entries) => Object.fromEntries(entries);

// src/styles/tailwindBase.ts
import aspectRatio from "@tailwindcss/aspect-ratio";
import animate from "tailwindcss-animate";

// src/constants/breakpoints.ts
var breakpoints = {
  xs: 0,
  sm: 393,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536,
  "3xl": 1728,
  "4xl": 1920,
  max: 2560
};

// src/constants/zIndex.ts
var current = 0;
var get = () => {
  current += 100;
  return current.toString();
};
var Z_INDEX = {
  sidebar: get(),
  overlay: get(),
  sheet: get(),
  modal: get(),
  tooltip: get(),
  toast: get()
};

// src/styles/tailwindBase.ts
var numberToPx = (n) => {
  return `${n}px`;
};
var COMMON_COLORS = {
  // General utility
  transparent: "transparent",
  current: "currentColor",
  inherit: "inherit",
  white: "rgb(var(--color-white) / <alpha-value>)",
  black: "rgb(var(--color-black) / <alpha-value>)",
  // Base background, but can be used for Text in certain situations (e.g inverse of primary text color)
  "base-1": "rgb(var(--color-base-1) / <alpha-value>)",
  "base-2": "rgb(var(--color-base-2) / <alpha-value>)",
  // Grays
  "gray-1": "rgb(var(--color-gray-1) / <alpha-value>)",
  "gray-2": "rgb(var(--color-gray-2) / <alpha-value>)",
  "gray-3": "rgb(var(--color-gray-3) / <alpha-value>)",
  // Blues
  "blue-1": "rgb(var(--color-blue-1) / <alpha-value>)",
  "blue-2": "rgb(var(--color-blue-2) / <alpha-value>)",
  "blue-3": "rgb(var(--color-blue-3) / <alpha-value>)",
  "blue-4": "rgb(var(--color-blue-4) / <alpha-value>)",
  "blue-5": "rgb(var(--color-blue-5) / <alpha-value>)",
  // Greens
  "green-1": "rgb(var(--color-green-1) / <alpha-value>)",
  "green-2": "rgb(var(--color-green-2) / <alpha-value>)",
  "green-3": "rgb(var(--color-green-3) / <alpha-value>)",
  // Yellows
  "yellow-1": "rgb(var(--color-yellow-1) / <alpha-value>)",
  "yellow-2": "rgb(var(--color-yellow-2) / <alpha-value>)",
  // Reds
  "red-1": "rgb(var(--color-red-1) / <alpha-value>)",
  "red-2": "rgb(var(--color-red-2) / <alpha-value>)",
  "red-3": "rgb(var(--color-red-3) / <alpha-value>)",
  // Accents
  "accent-red": "rgb(var(--color-accent-red) / <alpha-value>)",
  "accent-purple": "rgb(var(--color-accent-purple) / <alpha-value>)",
  "accent-fuchsia": "rgb(var(--color-accent-fuchsia) / <alpha-value>)",
  "accent-orange": "rgb(var(--color-accent-orange) / <alpha-value>)",
  "accent-cerulean": "rgb(var(--color-accent-cerulean) / <alpha-value>)",
  "accent-aqua": "rgb(var(--color-accent-aqua) / <alpha-value>)",
  // Aliases
  success: "rgb(var(--color-green-2) / <alpha-value>)",
  warning: "rgb(var(--color-yellow-1) / <alpha-value>)",
  error: "rgb(var(--color-red-2) / <alpha-value>)"
};
var TEXT_COLORS = {
  ...COMMON_COLORS,
  // Base text
  primary: "rgb(var(--color-text-primary) / <alpha-value>)",
  secondary: "rgb(var(--color-text-secondary) / <alpha-value>)",
  // Interactive text
  "interactive-primary": "rgb(var(--color-text-interactive-primary) / <alpha-value>)",
  "interactive-primary-hover": "rgb(var(--color-text-interactive-primary-hover) / <alpha-value>)",
  "interactive-secondary": "rgb(var(--color-text-interactive-secondary) / <alpha-value>)",
  "interactive-secondary-hover": "rgb(var(--color-text-interactive-secondary-hover) / <alpha-value>)",
  // Interactive icons
  "interactive-icon-primary": "rgb(var(--color-interactive-icon-primary) / <alpha-value>)",
  "interactive-icon-primary-hover": "rgb(var(--color-interactive-icon-primary-hover) / <alpha-value>)",
  "interactive-icon-secondary": "rgb(var(--color-interactive-icon-secondary) / <alpha-value>)",
  "interactive-icon-secondary-hover": "rgb(var(--color-interactive-icon-secondary-hover) / <alpha-value>)",
  // Used to match elevation background color for usages related to currentColor i.e. svgs
  "bg-elevation-1": "rgb(var(--color-elevation-1) / <alpha-value>)",
  "bg-elevation-1-subtle": "rgb(var(--color-elevation-1-subtle) / <alpha-value>)",
  "bg-elevation-2": "rgb(var(--color-elevation-2) / <alpha-value>)",
  "bg-elevation-3": "rgb(var(--color-elevation-3) / <alpha-value>)"
};
var BORDER_COLORS = {
  ...COMMON_COLORS,
  // Naming primarily to not conflict with border size presets
  "level-1": "var(--color-border-1)",
  "level-2": "var(--color-border-2)",
  "level-3": "var(--color-border-3)",
  "text-primary": "rgb(var(--color-text-primary) / <alpha-value>)",
  "text-secondary": "rgb(var(--color-text-secondary) / <alpha-value>)"
};
var tailwindBase = {
  content: [],
  darkMode: "class",
  theme: {
    colors: {
      ...COMMON_COLORS,
      // Component background grays
      "component-gray-1": "var(--color-component-gray-1)",
      "component-gray-2": "var(--color-component-gray-2)",
      "component-gray-3": "var(--color-component-gray-3)",
      "component-gray-4": "var(--color-component-gray-4)",
      "component-gray-1-solid": "rgb(var(--color-component-gray-1-solid) / <alpha-value>)",
      "component-gray-2-solid": "rgb(var(--color-component-gray-2-solid) / <alpha-value>)",
      // Elevation
      "elevation-1": "rgb(var(--color-elevation-1) / <alpha-value>)",
      "elevation-1-subtle": "rgb(var(--color-elevation-1-subtle) / <alpha-value>)",
      "elevation-2": "rgb(var(--color-elevation-2) / <alpha-value>)",
      "elevation-3": "rgb(var(--color-elevation-3) / <alpha-value>)",
      // In case we need to use border colors as background colors e.g. Separator
      "border-1": "var(--color-border-1)",
      "border-2": "var(--color-border-2)",
      "border-3": "var(--color-border-3)",
      // In case we need to use text colors as background colors, ex. underlines
      "text-primary": "rgb(var(--color-text-primary) / <alpha-value>)",
      "text-secondary": "rgb(var(--color-text-secondary) / <alpha-value>)"
    },
    textColor: TEXT_COLORS,
    borderColor: BORDER_COLORS,
    fill: TEXT_COLORS,
    boxShadow: {
      // Default tailwind shadow-none
      none: "box-shadow: 0 0 #0000",
      "elevation-1": "var(--shadow-elevation-1)",
      "elevation-1-subtle": "var(--shadow-elevation-1-subtle)",
      "elevation-2": "var(--shadow-elevation-2)",
      "elevation-3": "var(--shadow-elevation-3)"
    },
    fontFamily: {
      inter: "var(--font-family-inter)",
      "ibm-plex-mono": "var(--font-family-ibm-plex-mono)"
    },
    screens: {
      ...Object.fromEntries(
        Object.entries(breakpoints).map(([key, value]) => [
          key,
          numberToPx(value)
        ])
      ),
      // Fixed-range breakpoints
      "xs-sm": {
        min: numberToPx(breakpoints.xs),
        max: numberToPx(breakpoints.sm - 1)
      },
      "xs-lg": {
        min: numberToPx(breakpoints.xs),
        max: numberToPx(breakpoints.lg - 1)
      }
      // NOTE: Add more ranges here as needed (e.g. sm-md, xs-lg, etc.)
    },
    fontSize: {
      // Body
      xs: "12px",
      sm: "14px",
      md: "16px",
      // Heading
      "heading-xs": "18px",
      "heading-sm": "20px",
      "heading-md": "24px",
      "heading-lg": "32px",
      // Display
      "display-sm": "24px",
      "display-md": "32px",
      "display-lg": "48px",
      "display-xl": "60px"
    },
    aspectRatio: {
      auto: "auto",
      square: "1 / 1",
      video: "16 / 9",
      1: "1",
      2: "2",
      3: "3",
      4: "4",
      5: "5",
      6: "6",
      7: "7",
      8: "8",
      9: "9",
      10: "10",
      11: "11",
      12: "12",
      13: "13",
      14: "14",
      15: "15",
      16: "16"
    },
    extend: {
      borderRadius: {
        inherit: "inherit",
        initial: "initial",
        // This default value is in flight (e.g. in discussions to remove 10px entirely), but is what we had before as a "default"
        // so keeping it for now
        default: "10px"
      },
      fontWeight: {
        inherit: "inherit"
      },
      transitionProperty: {
        width: "width"
      },
      zIndex: Z_INDEX
    }
  },
  plugins: [aspectRatio, animate],
  safelist: [
    // Safelisting text colors so we can use them as `color` props in Components e.g. Text
    ...Object.keys(TEXT_COLORS).map((key) => `text-${key}`),
    // Safelisting fill colors so we can use them as `fill` props in Components e.g. SVGs
    ...Object.keys(TEXT_COLORS).map((key) => `fill-${key}`),
    // Safelisting border colors so we can use them as `color` props in Components e.g. Spinner
    ...Object.keys(BORDER_COLORS).map((key) => `border-${key}`),
    // Safelist aspect-ratio classes so they can be used dynamically at runtime (some uses require response from API)
    {
      pattern: /aspect-+/
    },
    // Safelist object fit classes to be used dynamically.
    ...[
      "object-contain",
      "object-cover",
      "object-fill",
      "object-none",
      "object-scale-down"
    ]
  ],
  future: {
    // This makes it such that hover styles are only applied when the user's browser supports hover e.g. desktop
    // https://github.com/tailwindlabs/tailwindcss/pull/8394#issue-1243295337
    hoverOnlyWhenSupported: true
  }
};

// src/styles/utils/interactive.ts
import { tv } from "tailwind-variants";
var interactiveVariants = tv({
  base: "cursor-pointer",
  variants: {
    text: {
      primary: "text-interactive-primary hover:text-interactive-primary-hover",
      secondary: "text-interactive-secondary hover:text-interactive-secondary-hover"
    },
    icon: {
      primary: "text-interactive-icon-primary hover:text-interactive-icon-primary-hover",
      secondary: "text-interactive-icon-secondary hover:text-interactive-icon-secondary-hover"
    },
    iconFill: {
      primary: "fill-interactive-icon-primary hover:fill-interactive-icon-primary-hover",
      secondary: "fill-interactive-icon-secondary hover:fill-interactive-icon-secondary-hover"
    }
  }
});

// src/styles/utils/color.ts
var allTailwindTextColors = fromEntries(
  keys(tailwindBase.theme.textColor).map((color) => [color, `text-${color}`])
);
var tailwindTextColors = allTailwindTextColors;
var textColor = {
  ...tailwindTextColors,
  // Special "color"s that handle the whole primary and secondary interactive styles
  "interactive-primary-styles": interactiveVariants({
    text: "primary"
  }),
  "interactive-secondary-styles": interactiveVariants({
    text: "secondary"
  }),
  "interactive-icon-primary-styles": interactiveVariants({
    icon: "primary"
  }),
  "interactive-icon-secondary-styles": interactiveVariants({
    icon: "secondary"
  })
};
var textColorVariants = tv2({
  variants: {
    color: textColor
  }
});
var borderColorVariants = tv2({
  variants: {
    color: fromEntries(
      keys(tailwindBase.theme.borderColor).map((color) => [
        color,
        `border-${color}`
      ])
    )
  }
});

// src/styles/utils/disabled.ts
import { tv as tv3 } from "tailwind-variants";
var disabledVariants = tv3({
  base: "disabled:pointer-events-none disabled:opacity-40",
  variants: {
    disabled: {
      true: "pointer-events-none opacity-40"
    }
  }
});

// src/styles/utils/input.ts
import { tv as tv5 } from "tailwind-variants";

// src/utils/classNames.ts
import { clsx } from "clsx";

// src/utils/twMerge.ts
import { extendTailwindMerge } from "tailwind-merge";
var twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [
        {
          text: [
            "xs",
            "sm",
            "md",
            "heading-xs",
            "heading-sm",
            "heading-md",
            "heading-lg",
            "display-sm",
            "display-md",
            "display-lg",
            "display-xl"
          ]
        }
      ],
      z: [
        {
          z: Object.keys(Z_INDEX)
        }
      ],
      "font-weight": ["font-inherit"],
      rounded: ["rounded-inherit", "rounded-initial"]
    }
  }
});

// src/utils/classNames.ts
var classNames = (...inputs) => {
  return twMerge(clsx(inputs));
};

// src/utils/twDataIdSelector.ts
var twDataIdSelector = (_componentWithDataId, arg) => arg;

// src/utils/index.ts
import { tv as tv4 } from "tailwind-variants";

// src/styles/utils/input.ts
var inputVariants = tv5({
  base: classNames(
    "flex h-10 w-full items-center rounded py-2 pl-4 pr-1 text-md",
    "border border-level-2 transition duration-200 hover:border-level-3",
    "dark:border-0 dark:bg-component-gray-1",
    "focus-within:shadow-none focus-within:hover:shadow-none focus-within:border-level-3",
    "focus:outline-none"
  ),
  variants: {
    disabled: {
      true: classNames(
        "pointer-events-none border-level-2 bg-component-gray-1 text-secondary dark:bg-component-gray-2"
      )
    },
    error: {
      true: "border-error"
    }
  }
});
var preventInputZoomStyles = classNames(
  "min-h-[26px] text-[16px] leading-[26px]",
  "sm:font-[inherit] sm:leading-[inherit] sm:text-[inherit]"
);

// src/styles/utils/link.ts
import { tv as tv6 } from "tailwind-variants";
var linkVariants = tv6({
  base: "cursor-pointer no-underline",
  variants: {
    variant: {
      default: interactiveVariants({ text: "primary" }),
      subtle: classNames(
        "font-semibold",
        interactiveVariants({ text: "secondary" })
      ),
      unstyled: ""
    }
  }
});

// src/styles/utils/objectFit.ts
import { tv as tv7 } from "tailwind-variants";
var objectFitVariants = tv7({
  variants: {
    objectFit: {
      contain: "object-contain",
      cover: "object-cover",
      fill: "object-fill",
      none: "object-none",
      "scale-down": "object-scale-down"
    }
  }
});

// src/styles/utils/overlay.ts
var overlayStyles = classNames(
  "fixed inset-0 z-overlay bg-gray-3/80",
  "data-[state=open]:animate-in data-[state=closed]:animate-out",
  "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
);

// src/utils/element.ts
var isClickableElement = (props) => {
  return Boolean(props.onClick);
};
var isLinkElement = (props) => {
  return Boolean(props.href);
};
var isInteractiveElement = (props) => {
  return isLinkElement(props) || isClickableElement(props);
};

// src/components/Link/Link.react.tsx
import { sanitizeUrl } from "@braintree/sanitize-url";
import React, { forwardRef } from "react";
var Link = forwardRef(function Link2({ className, href, variant = "default", disabled, rel, target, ...rest }, ref) {
  if (href && !href.startsWith("/")) {
    target = target != null ? target : "_blank";
    rel = rel != null ? rel : "nofollow noopener";
  }
  return /* @__PURE__ */ React.createElement(
    "a",
    {
      "aria-disabled": disabled != null ? disabled : void 0,
      className: classNames(
        linkVariants({ variant }),
        disabledVariants({ disabled }),
        className
      ),
      href: href ? sanitizeUrl(href) : void 0,
      ref,
      rel,
      tabIndex: disabled ? -1 : void 0,
      target,
      ...rest
    }
  );
});

// src/components/UnstyledButton/UnstyledButton.react.tsx
import { Slot } from "@radix-ui/react-slot";
import React2, { forwardRef as forwardRef2 } from "react";
var UnstyledButtonDataId = "UnstyledButton";
var UnstyledButton = Object.assign(
  forwardRef2(function UnstyledButton2({ asChild, className, ...rest }, ref) {
    const Comp = asChild ? Slot : "button";
    return /* @__PURE__ */ React2.createElement(
      Comp,
      {
        className: classNames(
          "inline-flex items-center border-0 bg-inherit font-[inherit] text-[100%]",
          className
        ),
        "data-id": UnstyledButtonDataId,
        ref,
        type: "button",
        ...rest
      }
    );
  }),
  { dataId: UnstyledButtonDataId }
);

// src/components/IconButton/IconButton.react.tsx
var LinkIconButton = forwardRef3(
  function LinkIconButton2({ renderLink, ...rest }, ref) {
    const Comp = renderLink != null ? renderLink : Link;
    return /* @__PURE__ */ React3.createElement(Comp, { variant: "unstyled", ...rest, ref });
  }
);
var iconButtonVariants = tv8({
  variants: {
    background: {
      transparent: "active:bg-component-gray-1",
      opaque: "bg-component-gray-2 active:bg-component-gray-3"
    }
  }
});
var IconButton = forwardRef3(function IconButton2({
  children,
  background = "transparent",
  disabled,
  className: propsClassName,
  renderLink,
  ...rest
}, ref) {
  const className = classNames(
    "flex items-center justify-center outline-none",
    "size-11 rounded-full",
    iconButtonVariants({ background }),
    disabledVariants({ disabled }),
    propsClassName
  );
  if (isLinkElement(rest)) {
    return /* @__PURE__ */ React3.createElement(
      LinkIconButton,
      {
        ...rest,
        className,
        ref,
        renderLink
      },
      children
    );
  }
  return /* @__PURE__ */ React3.createElement(
    UnstyledButton,
    {
      className,
      disabled,
      ...rest,
      ref
    },
    children
  );
});

// src/components/BottomNavigationBar/BottomNavigationBar.react.tsx
import React12, {
  forwardRef as forwardRef12
} from "react";
import { tv as tv11 } from "tailwind-variants";

// src/components/Item/Item.react.tsx
import React11, { forwardRef as forwardRef11 } from "react";
import { tv as tv10 } from "tailwind-variants";

// src/components/Avatar/Avatar.react.tsx
import React9, {
  forwardRef as forwardRef9
} from "react";

// src/components/CenterAligned/CenterAligned.react.tsx
import React8, { forwardRef as forwardRef8 } from "react";

// src/components/VerticalAligned/VerticalAligned.react.tsx
import React7, { forwardRef as forwardRef7 } from "react";

// src/components/FlexColumn/FlexColumn.react.tsx
import React6, { forwardRef as forwardRef6 } from "react";

// src/components/Flex/Flex.react.tsx
import React5, { forwardRef as forwardRef5 } from "react";

// src/components/Block/Block.react.tsx
import React4, { forwardRef as forwardRef4 } from "react";

// src/components/Slot/index.ts
import { Slot as Slot2 } from "@radix-ui/react-slot";

// src/components/Block/Block.react.tsx
var Block = forwardRef4(function Block2({ asChild, color, className, ...rest }, ref) {
  const Comp = asChild ? Slot2 : "div";
  return /* @__PURE__ */ React4.createElement(
    Comp,
    {
      className: classNames(textColorVariants({ color }), className),
      ref,
      ...rest
    }
  );
});

// src/components/Flex/Flex.react.tsx
var Flex = forwardRef5(function Flex2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React5.createElement(Block, { className: classNames("flex", className), ref, ...rest });
});

// src/components/FlexColumn/FlexColumn.react.tsx
var FlexColumn = forwardRef6(
  function FlexColumn2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React6.createElement(Flex, { className: classNames("flex-col", className), ref, ...rest });
  }
);

// src/components/VerticalAligned/VerticalAligned.react.tsx
var VerticalAligned = forwardRef7(
  function VerticalAligned2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React7.createElement(
      FlexColumn,
      {
        className: classNames("justify-center", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/CenterAligned/CenterAligned.react.tsx
var CenterAligned = forwardRef8(
  function CenterAligned2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React8.createElement(
      VerticalAligned,
      {
        className: classNames("items-center", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Avatar/Avatar.react.tsx
var isImage = (props) => {
  return props.src !== void 0;
};
var isIcon = (props) => {
  return props.icon !== void 0;
};
var isVideo = (props) => {
  return [".webm", ".mp4", ".m4v", ".ogg", ".ogv", ".mov"].some(
    (ext) => {
      var _a;
      return (_a = props.src) == null ? void 0 : _a.toLowerCase().endsWith(ext);
    }
  );
};
var ImgAvatar = forwardRef9(
  function ImgAvatar2({ renderImage, ...rest }, ref) {
    const Comp = renderImage != null ? renderImage : "img";
    return /* @__PURE__ */ React9.createElement(Comp, { ...rest, ref });
  }
);
var VideoAvatar = forwardRef9(
  function VideoAvatar2(props, ref) {
    return /* @__PURE__ */ React9.createElement("video", { ...props, ref });
  }
);
var AvatarDataId = "Avatar";
var Avatar = Object.assign(
  forwardRef9(
    function Avatar2({
      className: propsClassName,
      renderImage,
      rounded,
      size = 24,
      style,
      ...props
    }, ref) {
      const className = classNames(
        "overflow-hidden",
        rounded && "rounded-full",
        propsClassName
      );
      const sizeStyles = {
        width: `${size}px`,
        height: `${size}px`,
        ...style
      };
      if (isVideo(props)) {
        return /* @__PURE__ */ React9.createElement(
          VideoAvatar,
          {
            className,
            "data-id": AvatarDataId,
            ref,
            size,
            style: sizeStyles,
            ...props
          }
        );
      } else if (isImage(props)) {
        const { objectFit, ...rest } = props;
        return /* @__PURE__ */ React9.createElement(
          ImgAvatar,
          {
            className: classNames(objectFitVariants({ objectFit }), className),
            "data-id": AvatarDataId,
            ref,
            renderImage,
            size,
            style: sizeStyles,
            ...rest
          }
        );
      } else if (isIcon(props)) {
        const { icon: IconComponent } = props;
        return /* @__PURE__ */ React9.createElement(
          IconComponent,
          {
            className,
            "data-id": AvatarDataId,
            size,
            title: props.title
          }
        );
      } else {
        return /* @__PURE__ */ React9.createElement(
          CenterAligned,
          {
            className,
            "data-id": AvatarDataId,
            ref,
            style: sizeStyles,
            ...props
          }
        );
      }
    }
  ),
  { dataId: AvatarDataId }
);

// src/components/Text/Text.react.tsx
import React10, { forwardRef as forwardRef10 } from "react";
import { tv as tv9 } from "tailwind-variants";
var textBodyVariants = tv9({
  base: "leading-normal",
  variants: {
    size: {
      tiny: "text-xs",
      small: "text-sm",
      medium: "text-md"
    },
    weight: {
      regular: "font-normal",
      semibold: "font-semibold"
    },
    responsive: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "small",
      responsive: true,
      className: "text-xs lg:text-sm"
    },
    {
      size: "medium",
      responsive: true,
      className: "text-sm lg:text-md"
    }
  ]
});
var BodyDataId = "TextBody";
var TextBody = Object.assign(
  forwardRef10(function TextBody2({
    asChild,
    className,
    weight,
    responsive = true,
    color,
    size = "medium",
    ...rest
  }, ref) {
    const Comp = asChild ? Slot2 : "span";
    return /* @__PURE__ */ React10.createElement(
      Comp,
      {
        className: classNames(
          textBodyVariants({
            weight,
            size,
            responsive
          }),
          textColorVariants({ color }),
          className
        ),
        "data-id": BodyDataId,
        ref,
        ...rest
      }
    );
  }),
  {
    dataId: BodyDataId
  }
);
var headingVariants = tv9({
  variants: {
    size: {
      tiny: "text-heading-xs",
      small: "text-heading-sm",
      medium: "text-heading-md",
      large: "text-heading-lg"
    },
    responsive: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "small",
      responsive: true,
      className: "text-heading-xs lg:text-heading-sm"
    },
    {
      size: "medium",
      responsive: true,
      className: "text-heading-xs lg:text-heading-sm 2xl:text-heading-md"
    },
    {
      size: "large",
      responsive: true,
      className: "text-heading-sm md:text-heading-md lg:text-heading-lg"
    }
  ]
});
var HeadingDataId = "TextHeading";
var TextHeading = Object.assign(
  forwardRef10(function TextHeading2({ asChild, className, responsive = true, color, size, ...rest }, ref) {
    const Comp = asChild ? Slot2 : "span";
    return /* @__PURE__ */ React10.createElement(
      Comp,
      {
        className: classNames(
          "font-semibold",
          headingVariants({ size, responsive, className: "leading-none" }),
          textColorVariants({ color }),
          className
        ),
        "data-id": HeadingDataId,
        ref,
        ...rest
      }
    );
  }),
  { dataId: HeadingDataId }
);
var displayVariants = tv9({
  variants: {
    size: {
      small: "text-display-sm",
      medium: "text-display-md",
      large: "text-display-lg",
      xlarge: "text-display-xl"
    },
    responsive: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "medium",
      responsive: true,
      className: "text-display-sm md:text-display-md"
    },
    {
      size: "large",
      responsive: true,
      className: "text-display-sm md:text-display-md xl:text-display-lg"
    },
    {
      size: "xlarge",
      responsive: true,
      className: "text-display-md md:text-display-xl"
    }
  ]
});
var DisplayDataId = "TextDisplay";
var TextDisplay = Object.assign(
  forwardRef10(function TextDisplay2({ asChild, className, color, responsive = true, size, ...rest }, ref) {
    const Comp = asChild ? Slot2 : "span";
    return /* @__PURE__ */ React10.createElement(
      Comp,
      {
        className: classNames(
          "font-semibold",
          displayVariants({ size, responsive, className: "leading-none" }),
          textColorVariants({ color }),
          className
        ),
        "data-id": DisplayDataId,
        ref,
        ...rest
      }
    );
  }),
  { dataId: DisplayDataId }
);
var Text = Object.assign(TextBody, {
  Body: TextBody,
  Heading: TextHeading,
  Display: TextDisplay
});

// src/components/Item/Item.react.tsx
var itemVariants = tv10({
  variants: {
    active: {
      true: "bg-component-gray-2"
    },
    interactive: {
      true: "cursor-pointer hover:bg-component-gray-1 active:bg-component-gray-2"
    },
    variant: {
      unstyled: "border-0 p-0"
    }
  }
});
var LinkItem = forwardRef11(function LinkItem2({ renderLink, ...rest }, ref) {
  const Comp = renderLink != null ? renderLink : Link;
  return /* @__PURE__ */ React11.createElement(Comp, { ref, variant: "unstyled", ...rest });
});
var ItemDataId = "Item";
var ItemBase = Object.assign(
  forwardRef11(function Item({ asChild, children, variant, interactive, renderLink, ...props }, ref) {
    const isInteractive = interactive != null ? interactive : isInteractiveElement(props);
    const { className, disabled, href, target, onClick, ...rest } = props;
    const contentProps = { href, target, onClick, disabled };
    const renderContent = () => {
      if (asChild) {
        return children;
      }
      if (isLinkElement(contentProps)) {
        return /* @__PURE__ */ React11.createElement(
          LinkItem,
          {
            disabled: contentProps.disabled,
            href: contentProps.href,
            renderLink,
            target: contentProps.target
          },
          children
        );
      }
      if (isClickableElement(contentProps)) {
        return /* @__PURE__ */ React11.createElement(UnstyledButton, { disabled: contentProps.disabled }, children);
      }
      return /* @__PURE__ */ React11.createElement("div", null, children);
    };
    const isUnstyled = variant === "unstyled";
    return /* @__PURE__ */ React11.createElement(
      Slot2,
      {
        className: classNames(
          "flex w-full border border-level-2 p-4",
          itemVariants({
            active: !isUnstyled && rest.active,
            interactive: !isUnstyled && !disabled && isInteractive,
            variant
          }),
          className
        ),
        "data-id": ItemDataId,
        ref,
        onClick,
        ...rest
      },
      renderContent()
    );
  }),
  { dataId: ItemDataId }
);
var ItemAvatarDataId = "ItemAvatar";
var ItemAvatar = Object.assign(
  forwardRef11(function ItemAvatar2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React11.createElement(
      Avatar,
      {
        className: classNames(
          "order-2 mr-4 shrink-0 self-center text-primary",
          className
        ),
        "data-id": ItemAvatarDataId,
        ref,
        ...rest
      }
    );
  }),
  { dataId: ItemAvatarDataId }
);
var ItemContentDataId = "ItemContent";
var ItemContent = ({ className, ...rest }) => {
  return /* @__PURE__ */ React11.createElement(
    VerticalAligned,
    {
      className: classNames(
        "order-3 mr-4 overflow-hidden",
        "flex-auto items-start self-stretch",
        className
      ),
      "data-id": ItemContentDataId,
      ...rest
    }
  );
};
ItemContent.dataId = ItemContentDataId;
var ItemTitleDataId = "ItemTitle";
var ItemTitle = Object.assign(
  forwardRef11(function ItemTitle2({ color = "primary", weight = "semibold", ...rest }, ref) {
    return /* @__PURE__ */ React11.createElement(
      Text,
      {
        color,
        "data-id": ItemTitleDataId,
        weight,
        ...rest,
        ref
      }
    );
  }),
  { dataId: ItemTitleDataId }
);
var ItemDescription = forwardRef11(
  function ItemDescription2({ color = "secondary", size = "small", ...rest }, ref) {
    return /* @__PURE__ */ React11.createElement(Text, { color, size, ...rest, ref });
  }
);
var ItemSide = ({ className, ...rest }) => {
  return /* @__PURE__ */ React11.createElement(
    FlexColumn,
    {
      className: classNames(
        "order-4 max-w-[40%] overflow-hidden text-right",
        "flex-[0_0_auto] justify-center self-stretch ",
        className
      ),
      ...rest
    }
  );
};
var ItemAction = ({ className, ...rest }) => {
  return /* @__PURE__ */ React11.createElement(
    FlexColumn,
    {
      className: classNames("order-5 ml-3 justify-center", className),
      ...rest
    }
  );
};
var Item2 = Object.assign(ItemBase, {
  Avatar: ItemAvatar,
  Content: ItemContent,
  Title: ItemTitle,
  Description: ItemDescription,
  Side: ItemSide,
  Action: ItemAction
});

// src/components/BottomNavigationBar/BottomNavigationBar.react.tsx
var BottomNavigationBarBase = forwardRef12(function BottomNavigationBar({ children, className, ...rest }, ref) {
  return /* @__PURE__ */ React12.createElement("div", { className: "h-[58px]" }, /* @__PURE__ */ React12.createElement(
    "nav",
    {
      className: classNames(
        "flex justify-between",
        "fixed inset-x-0 bottom-0 h-[58px]",
        "border-t border-level-1 bg-base-1",
        className
      ),
      ref,
      ...rest
    },
    children
  ));
});
var bottomNavigationBarItemVariants = tv11({
  variants: {
    interactive: {
      true: "text-secondary hover:text-primary"
    },
    active: {
      true: "text-primary"
    }
  }
});
var BottomNavigationBarItemBase = forwardRef12(function BottomNavigationBarItem({ active, children, renderItem, className, interactive, ...rest }, ref) {
  const Comp = renderItem != null ? renderItem : Item2;
  const isInteractive = interactive != null ? interactive : isInteractiveElement(rest);
  return /* @__PURE__ */ React12.createElement(
    Comp,
    {
      className: classNames(
        "size-full",
        "flex flex-nowrap items-center justify-center",
        bottomNavigationBarItemVariants({ active, interactive: isInteractive }),
        className
      ),
      interactive,
      ref,
      variant: "unstyled",
      ...rest
    },
    children
  );
});
var BottomNavigationBarAvatar = ({
  className,
  ...rest
}) => {
  return /* @__PURE__ */ React12.createElement(
    Item2.Avatar,
    {
      className: classNames("m-0 text-current", className),
      ...rest
    }
  );
};
var BottomNavigationBarItem2 = Object.assign(
  BottomNavigationBarItemBase,
  {
    Avatar: BottomNavigationBarAvatar
  }
);
var BottomNavigationBar2 = Object.assign(BottomNavigationBarBase, {
  Item: BottomNavigationBarItem2
});

// src/components/Button/Button.react.tsx
import React14, {
  forwardRef as forwardRef13,
  isValidElement
} from "react";
import { tv as tv13 } from "tailwind-variants";

// src/components/Spinner/Spinner.react.tsx
import React13 from "react";
import { tv as tv12 } from "tailwind-variants";
var spinnerVariants = tv12({
  variants: {
    size: {
      xsmall: "size-4 border-2",
      small: "size-6 border-[3px]",
      medium: "size-12 border-4",
      large: "size-[72px] border-[5px]"
    }
  }
});
var Spinner = ({
  className,
  color = "text-primary",
  size = "small"
}) => {
  return /* @__PURE__ */ React13.createElement(
    "span",
    {
      "aria-live": "polite",
      className: classNames(
        "inline-block animate-spin rounded-full",
        "!border-b-transparent",
        spinnerVariants({
          size
        }),
        // Default to small spinners on mobile only, except for when it's explicitly set to xsmall
        size !== "xsmall" && "xs-sm:size-6 xs-sm:border-[3px]",
        borderColorVariants({ color }),
        className
      ),
      role: "status"
    }
  );
};

// src/components/Button/Button.react.tsx
var buttonVariants = tv13({
  variants: {
    variant: {
      primary: "bg-blue-3 text-white hover:bg-blue-2",
      secondary: "bg-component-gray-2 text-primary hover:bg-component-gray-3",
      destructive: "bg-red-3/[0.08] text-red-3 hover:bg-red-3/[0.16]"
    },
    size: {
      small: "h-9 gap-2.5 rounded px-3 py-2.5",
      large: "h-10 gap-2 rounded px-4 py-3"
    },
    iconSide: {
      left: "",
      right: ""
    },
    onlyIcon: {
      true: ""
    }
  },
  compoundVariants: [
    {
      size: "large",
      iconSide: "left",
      className: "pl-3 pr-4"
    },
    {
      size: "large",
      iconSide: "right",
      className: "pl-4 pr-3"
    },
    {
      size: "small",
      onlyIcon: true,
      className: "px-2"
    },
    {
      size: "large",
      onlyIcon: true,
      className: "px-3"
    }
  ]
});
var hasCustomIcon = (props) => {
  return isValidElement(props.icon);
};
var hasAnyIcon = (props) => {
  return props.icon !== void 0;
};
var LinkButton = ({ renderLink, ...rest }) => {
  const Comp = renderLink != null ? renderLink : Link;
  return /* @__PURE__ */ React14.createElement(Comp, { ...rest, variant: "unstyled" });
};
var ButtonDataId = "Button";
var Button = Object.assign(
  forwardRef13(function Button2({
    renderLink,
    children,
    className,
    disabled = false,
    href,
    variant = "primary",
    size = "large",
    isLoading,
    type = "button",
    iconSide = "left",
    ...propsWithIcon
  }, ref) {
    const hasIcon = hasAnyIcon(propsWithIcon);
    const onlyIcon = hasIcon && !children;
    const renderContent = () => {
      if (isLoading) {
        let color;
        switch (variant) {
          case "primary":
            color = "white";
            break;
          case "destructive":
            color = "red-3";
            break;
        }
        return /* @__PURE__ */ React14.createElement(Spinner, { color });
      }
      const renderIcon = (buttonProps) => {
        var _a;
        const iconSize = size === "small" ? 20 : 24;
        return /* @__PURE__ */ React14.createElement(Flex, { "aria-hidden": "true", className: "pointer-events-none max-h-6" }, hasCustomIcon(buttonProps) ? buttonProps.icon : /* @__PURE__ */ React14.createElement(
          buttonProps.icon,
          {
            size: iconSize,
            ...(_a = buttonProps.overrides) == null ? void 0 : _a.Icon.props
          }
        ));
      };
      return /* @__PURE__ */ React14.createElement(React14.Fragment, null, hasIcon && iconSide === "left" && renderIcon(propsWithIcon), children, hasIcon && iconSide === "right" && renderIcon(propsWithIcon));
    };
    const { icon: _, ...props } = propsWithIcon;
    return /* @__PURE__ */ React14.createElement(
      Slot2,
      {
        className: classNames(
          "inline-flex items-center justify-center whitespace-nowrap",
          "font-semibold transition duration-200",
          textBodyVariants({
            size: "small",
            weight: "semibold"
          }),
          buttonVariants({
            variant,
            size,
            iconSide: hasIcon && !onlyIcon ? iconSide : void 0,
            onlyIcon
          }),
          disabledVariants({ disabled }),
          className
        ),
        "data-id": ButtonDataId,
        ref,
        ...props
      },
      href ? /* @__PURE__ */ React14.createElement(LinkButton, { disabled, href, renderLink }, renderContent()) : /* @__PURE__ */ React14.createElement("button", { disabled, type }, renderContent())
    );
  }),
  { dataId: ButtonDataId }
);

// src/components/Card/Card.react.tsx
import React15, { forwardRef as forwardRef14 } from "react";
var CardBase = forwardRef14(function Card({ children, className, ...rest }, ref) {
  return /* @__PURE__ */ React15.createElement(
    FlexColumn,
    {
      className: classNames(
        "overflow-hidden rounded bg-elevation-1 shadow-elevation-1",
        className
      ),
      ref,
      ...rest
    },
    children
  );
});
var CardContent = Block;
var CardFooter = forwardRef14(
  function CardFooter2({ children, className, ...rest }, ref) {
    return /* @__PURE__ */ React15.createElement(
      FlexColumn,
      {
        className: classNames("p-2 pb-3", className),
        ...rest,
        ref
      },
      children
    );
  }
);
var Card2 = Object.assign(CardBase, {
  Content: CardContent,
  Footer: CardFooter
});

// src/components/Chip/Chip.react.tsx
import React17, { forwardRef as forwardRef16 } from "react";

// src/components/FlexCenter/FlexCenter.react.tsx
import React16, { forwardRef as forwardRef15 } from "react";
var FlexCenter = forwardRef15(
  function FlexCenter2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React16.createElement(
      Flex,
      {
        className: classNames("items-center", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Chip/Chip.react.tsx
var Chip = forwardRef16(function Chip2({ children, className, ...rest }, ref) {
  return /* @__PURE__ */ React17.createElement(
    FlexCenter,
    {
      className: classNames(
        "w-fit whitespace-nowrap rounded-full bg-component-gray-2 px-2 py-0.5 uppercase backdrop-blur-[20px]",
        className
      ),
      ref,
      ...rest
    },
    /* @__PURE__ */ React17.createElement(Text, { className: "opacity-80", size: "tiny", weight: "semibold" }, children)
  );
});

// src/components/Container/Container.react.tsx
import React18, { forwardRef as forwardRef17 } from "react";
var Container = forwardRef17(
  function Container2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React18.createElement(
      "div",
      {
        className: classNames(
          "mx-auto w-full max-w-[2560px]",
          "px-4 sm:px-8 2xl:px-16",
          className
        ),
        ref,
        ...rest
      }
    );
  }
);

// src/components/FlexEnd/FlexEnd.react.tsx
import React19, { forwardRef as forwardRef18 } from "react";
var FlexEnd = forwardRef18(
  function FlexEnd2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React19.createElement(
      Flex,
      {
        className: classNames("justify-end", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/FlexStart/FlexStart.react.tsx
import React20, { forwardRef as forwardRef19 } from "react";
var FlexStart = forwardRef19(
  function FlexStart2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React20.createElement(
      Flex,
      {
        className: classNames("justify-start", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/InlineFlex/InlineFlex.react.tsx
import React21, { forwardRef as forwardRef20 } from "react";
var InlineFlex = forwardRef20(
  function InlineFlex2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React21.createElement(
      Block,
      {
        className: classNames("inline-flex", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Skeleton/Skeleton.react.tsx
import React23, { forwardRef as forwardRef22 } from "react";

// src/components/SpaceBetween/SpaceBetween.react.tsx
import React22, { forwardRef as forwardRef21 } from "react";
var SpaceBetween = forwardRef21(
  function SpaceBetween2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React22.createElement(
      Flex,
      {
        className: classNames("justify-between", className),
        ref,
        ...rest
      }
    );
  }
);

// src/components/Skeleton/Skeleton.react.tsx
var SkeletonBase = forwardRef22(
  function Skeleton({ className, ...rest }, ref) {
    return /* @__PURE__ */ React23.createElement(
      FlexColumn,
      {
        className: classNames(
          "size-full shrink-0 grow rounded-inherit",
          "[&>:not(:first-child)]:mt-2",
          className
        ),
        ref,
        ...rest
      }
    );
  }
);
var defaultBlockStyles = classNames(
  "size-full animate-pulse bg-component-gray-3"
);
var SkeletonRow = SpaceBetween;
var SkeletonBlock = forwardRef22(
  function SkeletonBlock2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React23.createElement(
      Block,
      {
        className: classNames(defaultBlockStyles, "rounded-inherit", className),
        ref,
        ...rest
      }
    );
  }
);
var SkeletonCircle = forwardRef22(
  function SkeletonCircle2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React23.createElement(
      Block,
      {
        className: classNames(defaultBlockStyles, "rounded-full", className),
        ref,
        ...rest
      }
    );
  }
);
var SkeletonLine = forwardRef22(
  function SkeletonLine2({ className, ...rest }, ref) {
    return /* @__PURE__ */ React23.createElement(
      Block,
      {
        className: classNames(
          defaultBlockStyles,
          "h-[18px] rounded-full",
          className
        ),
        ref,
        ...rest
      }
    );
  }
);
var Skeleton2 = Object.assign(SkeletonBase, {
  Row: SkeletonRow,
  Line: SkeletonLine,
  Block: SkeletonBlock,
  Circle: SkeletonCircle
});

// src/components/TextArea/TextArea.react.tsx
import React24, { forwardRef as forwardRef23 } from "react";
var TextArea = forwardRef23(
  function TextArea2({ className, disabled, error, ...props }, ref) {
    return /* @__PURE__ */ React24.createElement(
      "textarea",
      {
        className: classNames(
          inputVariants({ disabled, error }),
          "h-auto w-full resize-y rounded-lg p-3 text-md placeholder:text-secondary",
          "bg-transparent outline-none",
          preventInputZoomStyles,
          className
        ),
        ref,
        ...props
      }
    );
  }
);

// src/components/VisuallyHidden/VisuallyHidden.react.tsx
import { Root as VisuallyHiddenRoot } from "@radix-ui/react-visually-hidden";
var VisuallyHidden = VisuallyHiddenRoot;

// src/components/Alert/Alert.react.tsx
import React25, { forwardRef as forwardRef24 } from "react";
var AlertBase = forwardRef24(
  function Alert({ className, ...rest }, ref) {
    return /* @__PURE__ */ React25.createElement(
      Flex,
      {
        className: classNames(
          "gap-3 rounded-xl bg-component-gray-1",
          "p-5 lg:p-6",
          className
        ),
        ref,
        role: "alert",
        ...rest
      }
    );
  }
);
var AlertIcon = ({
  value: IconComponent,
  ...rest
}) => {
  return /* @__PURE__ */ React25.createElement(IconComponent, { ...rest });
};
var AlertContent = forwardRef24(function AlertContent2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React25.createElement(
    FlexColumn,
    {
      className: classNames("gap-0.5", className),
      ref,
      ...rest
    }
  );
});
var AlertTitle = forwardRef24(
  function AlertTitle2({ weight = "semibold", ...rest }, ref) {
    return /* @__PURE__ */ React25.createElement(Text, { ref, responsive: true, weight, ...rest });
  }
);
var AlertDescription = forwardRef24(function AlertDescription2({ color = "secondary", size = "small", ...rest }, ref) {
  return /* @__PURE__ */ React25.createElement(Text, { color, ref, responsive: true, size, ...rest });
});
var Alert2 = Object.assign(AlertBase, {
  Icon: AlertIcon,
  Content: AlertContent,
  Title: AlertTitle,
  Body: AlertDescription
});

// src/components/Pill/Pill.react.tsx
import React27, { forwardRef as forwardRef25 } from "react";

// src/styles/utils/fill.ts
import { tv as tv14 } from "tailwind-variants";
var allTailwindFills = fromEntries(
  keys(tailwindBase.theme.fill).map((fill2) => [fill2, `fill-${fill2}`])
);
var tailwindFills = allTailwindFills;
var fill = {
  ...tailwindFills,
  // Special "fill"s that handle the whole primary and secondary interactive styles
  "interactive-icon-primary-styles": interactiveVariants({
    iconFill: "primary"
  }),
  "interactive-icon-secondary-styles": interactiveVariants({
    iconFill: "secondary"
  })
};
var fillVariants = tv14({
  variants: {
    fill
  }
});

// src/icons/material-symbols/outlined/Close.react.tsx
import React26 from "react";
var Close = ({
  size = 24,
  fill: fill2 = "current",
  className,
  title = "Close",
  ...rest
}) => {
  return /* @__PURE__ */ React26.createElement(
    "svg",
    {
      className: classNames(fillVariants({ fill: fill2 }), className),
      height: size,
      role: "img",
      viewBox: "0 -960 960 960",
      width: size,
      xmlns: "http://www.w3.org/2000/svg",
      ...rest
    },
    /* @__PURE__ */ React26.createElement("title", null, title),
    /* @__PURE__ */ React26.createElement("path", { d: "m256-200-56-56 224-224-224-224 56-56 224 224 224-224 56 56-224 224 224 224-56 56-224-224-224 224Z" })
  );
};

// src/components/Pill/Pill.react.tsx
var Pill = forwardRef25(
  function Pill2({ children, disabled, className, withCloseIcon = true, ...rest }, ref) {
    return /* @__PURE__ */ React27.createElement(
      UnstyledButton,
      {
        className: classNames(
          "flex h-8 w-fit items-center gap-2 whitespace-nowrap rounded bg-component-gray-1 p-2",
          disabledVariants({ disabled }),
          className
        ),
        disabled,
        ref,
        ...rest
      },
      /* @__PURE__ */ React27.createElement(Text, { size: "tiny", weight: "semibold" }, children),
      withCloseIcon && /* @__PURE__ */ React27.createElement(
        Close,
        {
          className: interactiveVariants({ icon: "primary" }),
          size: 16
        }
      )
    );
  }
);

// src/components/SwitchPill/SwitchPill.react.tsx
import React30, { forwardRef as forwardRef28 } from "react";
import { tv as tv17 } from "tailwind-variants";

// src/components/VirtualizedTable/components/VirtualizedTableCell.react.tsx
import React28, { forwardRef as forwardRef26 } from "react";
import { tv as tv15 } from "tailwind-variants";
var virtualizedTableCellVariants = tv15({
  variants: {
    align: {
      left: "justify-start",
      center: "justify-center",
      right: "justify-end"
    }
  }
});
var VirtualizedTableCell = forwardRef26(function VirtualizedTableCell2({
  children,
  className,
  stickyLeft,
  role = "cell",
  align = "left",
  onClick
}, ref) {
  return /* @__PURE__ */ React28.createElement(
    FlexCenter,
    {
      className: classNames(
        "w-[128px] shrink-0 grow-0 overflow-hidden text-sm last:pr-4",
        stickyLeft !== void 0 && "sticky z-[1] h-full bg-base-1",
        virtualizedTableCellVariants({ align }),
        className
      ),
      ref,
      role,
      style: {
        left: stickyLeft
      },
      onClick
    },
    children
  );
});

// src/components/VirtualizedTable/components/VirtualizedTableRow.react.tsx
import React29, { forwardRef as forwardRef27 } from "react";
import { tv as tv16 } from "tailwind-variants";

// src/components/VirtualizedTable/constants.ts
var DEFAULT_ITEM_HEIGHT_PX = 60;

// src/components/VirtualizedTable/components/VirtualizedTableRow.react.tsx
var virtualizedTableRowVariants = tv16({
  variants: {
    interactive: {
      true: classNames(
        "cursor-pointer hover:bg-component-gray-1",
        "active:bg-component-gray-2 [&>*]:bg-transparent"
      )
    },
    expanded: {
      true: "bg-component-gray-1 [&>*]:bg-transparent"
    }
  }
});
var VirtualizedTableRowBase = forwardRef27(
  function VirtualizedTableRowBase2({
    className,
    children,
    minHeight = DEFAULT_ITEM_HEIGHT_PX,
    more,
    onClick,
    interactive: paramInteractive,
    ...rest
  }, ref) {
    const interactive = paramInteractive != null ? paramInteractive : Boolean(onClick);
    const expanded = Boolean(more);
    return /* @__PURE__ */ React29.createElement(
      "div",
      {
        className: classNames("size-full min-w-fit"),
        ref,
        role: "row",
        style: { minHeight },
        onClick,
        ...rest
      },
      /* @__PURE__ */ React29.createElement(
        InlineFlex,
        {
          className: classNames(
            "min-w-full items-center gap-4",
            virtualizedTableRowVariants({ interactive, expanded }),
            className
          ),
          style: { height: minHeight }
        },
        children
      ),
      more && /* @__PURE__ */ React29.createElement(Flex, { className: "h-full rounded-b-xl bg-elevation-3" }, more)
    );
  }
);
var VirtualizedTableRow = Object.assign(VirtualizedTableRowBase, {
  Cell: VirtualizedTableCell
});

// src/components/SwitchPill/SwitchPill.react.tsx
var switchPillVariants = tv17({
  variants: {
    checked: {
      false: "bg-component-gray-1 text-secondary hover:bg-component-gray-2 active:bg-component-gray-3",
      true: "bg-component-gray-3 font-semibold"
    }
  }
});
var SwitchPill = forwardRef28(
  function SwitchPill2({ className, children, checked = false, ...rest }, ref) {
    return /* @__PURE__ */ React30.createElement(
      UnstyledButton,
      {
        "aria-checked": Boolean(checked),
        className: classNames(
          "h-8 items-center justify-center gap-2.5 whitespace-nowrap rounded",
          "px-4 py-3",
          textBodyVariants({ size: "tiny" }),
          switchPillVariants({ checked }),
          className
        ),
        ref,
        role: "switch",
        ...rest
      },
      children
    );
  }
);

// src/components/TabMenu/TabMenu.react.tsx
import React31, { forwardRef as forwardRef29 } from "react";
import { tv as tv18 } from "tailwind-variants";
var TabMenuBase = forwardRef29(
  function TabMenu({ children, className, ...rest }, ref) {
    return /* @__PURE__ */ React31.createElement("nav", { className: classNames("flex gap-1", className), ref, ...rest }, children);
  }
);
var tabMenuItemVariants = tv18({
  variants: {
    active: {
      true: "bg-component-gray-2 font-semibold text-primary"
    },
    blur: {
      true: "dark border bg-white/[.02] backdrop-blur-md backdrop-filter"
    }
  },
  compoundVariants: [
    {
      active: true,
      blur: true,
      class: "border-transparent bg-component-gray-3"
    }
  ]
});
var TabMenuItemBase = forwardRef29(
  function TabMenuItem({ className, active, blur, ...rest }, ref) {
    return /* @__PURE__ */ React31.createElement(
      Item2,
      {
        "aria-current": active ? "page" : void 0,
        className: classNames(
          "w-fit whitespace-nowrap rounded-full px-6 py-2 font-normal",
          "text-secondary hover:text-primary",
          "focus-visible:text-primary focus-visible:outline-none",
          tabMenuItemVariants({ active, blur }),
          className
        ),
        ref,
        variant: "unstyled",
        ...rest
      }
    );
  }
);
var TabMenuItemTitle = forwardRef29(function TabMenuItemTitle2({ className, ...rest }, ref) {
  return /* @__PURE__ */ React31.createElement(
    Text.Body,
    {
      className: classNames("font-inherit", className),
      ref,
      ...rest
    }
  );
});
var TabMenuItem2 = Object.assign(TabMenuItemBase, {
  Title: TabMenuItemTitle
});
var TabMenu2 = Object.assign(TabMenuBase, {
  Item: TabMenuItem2
});

// src/styles/tailwindSetup.ts
import path from "path";

// package.json
var package_default = {
  name: "@opensea/ui-kit",
  version: "0.2.58",
  description: "OpenSea's Design System implementation in React",
  license: "MIT",
  author: "OpenSea Developers",
  sideEffects: false,
  exports: {
    ".": {
      require: "./dist/index.js",
      default: "./dist/index.mjs"
    },
    "./client": {
      require: "./client/index.js",
      default: "./client/index.mjs"
    },
    "./icons": {
      require: "./icons/index.js",
      module: "./icons/index.mjs",
      default: "./icons/index.mjs"
    },
    "./styles/globals.css": {
      require: "./src/styles/globals.css",
      default: "./src/styles/globals.css"
    }
  },
  main: "./dist/index.js",
  module: "./dist/index.mjs",
  types: "./dist/index.d.ts",
  files: [
    "./dist",
    "./client",
    "./src",
    "./icons"
  ],
  scripts: {
    build: "pnpm prepack && tsup && pnpm postpack",
    "build-storybook": "storybook build",
    "build:check-types": "attw --pack --entrypoints . client icons",
    "eslint:check": "eslint src -max-warnings 0 --ext .ts,.tsx",
    "eslint:fix": "eslint . --fix",
    generate: "npx plop",
    lint: "pnpm typecheck",
    prepack: `sed -i.bak -e 's|"main": "./src/index.ts"|"main": "./dist/index.js"|; s|"default": "./src/index.ts"|"default": "./dist/index.mjs"|; s|"default": "./src/client.ts"|"default": "./client/index.mjs"|; s|"types": "./src/index.ts"|"types": "./dist/index.d.ts"|' package.json && rm package.json.bak`,
    postpack: `sed -i.bak -e 's|"main": "./dist/index.js"|"main": "./src/index.ts"|; s|"default": "./dist/index.mjs"|"default": "./src/index.ts"|; s|"default": "./client/index.mjs"|"default": "./src/client.ts"|; s|"types": "./dist/index.d.ts"|"types": "./src/index.ts"|' package.json && rm package.json.bak`,
    storybook: "storybook dev -p 6006",
    "test:unit": "vitest",
    "test:visual": "playwright test",
    "test:visual:update": "playwright test -u",
    typecheck: "tsc"
  },
  dependencies: {
    "@artsy/fresnel": "^7.1.3",
    "@braintree/sanitize-url": "^7.0.0",
    "@radix-ui/react-aspect-ratio": "^1.0.3",
    "@radix-ui/react-checkbox": "^1.0.4",
    "@radix-ui/react-collapsible": "^1.0.3",
    "@radix-ui/react-dialog": "^1.0.5",
    "@radix-ui/react-dropdown-menu": "^2.0.6",
    "@radix-ui/react-label": "^2.0.2",
    "@radix-ui/react-popover": "^1.0.7",
    "@radix-ui/react-progress": "^1.0.3",
    "@radix-ui/react-radio-group": "^1.1.3",
    "@radix-ui/react-separator": "^1.0.3",
    "@radix-ui/react-slider": "^1.1.2",
    "@radix-ui/react-slot": "^1.0.2",
    "@radix-ui/react-switch": "^1.0.3",
    "@radix-ui/react-toast": "^1.1.5",
    "@radix-ui/react-toggle-group": "^1.0.4",
    "@radix-ui/react-tooltip": "^1.0.7",
    "@radix-ui/react-visually-hidden": "^1.0.3",
    "@react-hook/merged-ref": "^1.3.2",
    "@tailwindcss/aspect-ratio": "^0.4.2",
    "@tanstack/react-virtual": "3.0.2",
    clsx: "^2.0.0",
    cmdk: "^0.2.0",
    "embla-carousel-auto-height": "^8.0.0-rc19",
    "embla-carousel-autoplay": "^8.0.0-rc19",
    "embla-carousel-class-names": "^8.0.0-rc19",
    "embla-carousel-react": "^8.0.0-rc19",
    "lodash.throttle": "^4.1.1",
    polished: "^4.2.2",
    "react-international-phone": "^4.0.4",
    "react-intersection-observer": "^9.5.2",
    "tailwind-merge": "^2.0.0",
    "tailwind-variants": "^0.1.20",
    "tailwindcss-animate": "^1.0.7",
    "use-context-selector": "^1.4.1"
  },
  devDependencies: {
    "@arethetypeswrong/cli": "0.13.5",
    "@faker-js/faker": "8.3.1",
    "@opensea/testing-utils": "workspace:*",
    "@playwright/test": "1.41.1",
    "@storybook/addon-essentials": "7.6.10",
    "@storybook/addon-interactions": "7.6.10",
    "@storybook/addon-links": "7.6.10",
    "@storybook/addon-storysource": "7.6.10",
    "@storybook/addon-styling": "1.3.7",
    "@storybook/blocks": "7.6.10",
    "@storybook/csf": "0.1.2",
    "@storybook/nextjs": "7.6.10",
    "@storybook/react": "7.6.10",
    "@storybook/source-loader": "7.6.10",
    "@storybook/testing-library": "0.2.2",
    "@storybook/types": "7.6.10",
    "@testing-library/jest-dom": "6.2.0",
    "@testing-library/react": "14.1.2",
    "@testing-library/user-event": "14.5.2",
    "@types/lodash": "4.14.202",
    "@types/lodash.throttle": "4.1.9",
    "@types/node": "20.11.5",
    "@types/react": "18.2.42",
    "@vitejs/plugin-react": "4.2.1",
    autoprefixer: "10.4.17",
    cheerio: "1.0.0-rc.12",
    "date-fns": "3.3.0",
    "dd-trace": "5.0.0",
    "embla-carousel": "8.0.0-rc19",
    jsdom: "24.0.0",
    lodash: "4.17.21",
    next: "14.1.0",
    plop: "4.0.1",
    postcss: "8.4.33",
    react: "18.2.0",
    "react-dom": "18.2.0",
    "react-hook-form": "7.49.3",
    "resize-observer-polyfill": "1.5.1",
    storybook: "7.6.10",
    tailwindcss: "3.4.1",
    tsup: "8.0.1",
    typescript: "5.3.3",
    "use-debounce": "10.0.0",
    "vite-tsconfig-paths": "4.3.1",
    vitest: "1.2.1"
  },
  peerDependencies: {
    react: "*",
    tailwindcss: "*"
  }
};

// src/styles/tailwindSetup.ts
var getTailwindContentFiles = () => {
  const uiKitBase = path.dirname(path.dirname(__require.resolve(package_default.name)));
  return package_default.files.filter((file) => file !== "./src").map((file) => path.join(uiKitBase, file, "**/*.js"));
};
export {
  Alert2 as Alert,
  AlertContent,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Avatar,
  Block,
  BottomNavigationBar2 as BottomNavigationBar,
  BottomNavigationBarAvatar,
  BottomNavigationBarItem2 as BottomNavigationBarItem,
  Button,
  Card2 as Card,
  CardContent,
  CardFooter,
  CenterAligned,
  Chip,
  Container,
  Flex,
  FlexCenter,
  FlexColumn,
  FlexEnd,
  FlexStart,
  IconButton,
  InlineFlex,
  Item2 as Item,
  ItemAction,
  ItemAvatar,
  ItemContent,
  ItemDescription,
  ItemSide,
  ItemTitle,
  Link,
  Pill,
  Skeleton2 as Skeleton,
  SkeletonBlock,
  SkeletonCircle,
  SkeletonLine,
  SkeletonRow,
  Slot2 as Slot,
  SpaceBetween,
  Spinner,
  SwitchPill,
  TabMenu2 as TabMenu,
  TabMenuItem2 as TabMenuItem,
  Text,
  TextArea,
  TextBody,
  TextDisplay,
  TextHeading,
  UnstyledButton,
  VerticalAligned,
  VirtualizedTableCell,
  VirtualizedTableRow,
  VisuallyHidden,
  borderColorVariants,
  classNames,
  disabledVariants,
  getTailwindContentFiles,
  inputVariants,
  interactiveVariants,
  isVideo,
  linkVariants,
  objectFitVariants,
  overlayStyles,
  preventInputZoomStyles,
  tailwindBase,
  textBodyVariants,
  textColorVariants,
  tv4 as tv,
  twDataIdSelector
};
//# sourceMappingURL=index.mjs.map